import { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../../config/firebase';
import { Spinner } from '../../components/ui/Spinner';
import { useShoppingCart } from '../../contexts/ShoppingContext';
import OrderConfirmationDialog from '../../components/store/OrderConfirmationDialog';
import ProductCard from '../../components/products/ProductCard';

const StorePage = () => {
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { cart, clearCart } = useShoppingCart();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsRef = ref(db, 'products/products');
        const snapshot = await get(productsRef);
        if (snapshot.exists()) {
          setProducts(snapshot.val());
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to load products');
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleOrder = (formData) => {
    const encodedMessage = encodeURIComponent(formData.message);
    window.open(`https://wa.me/94776620717?text=${encodedMessage}`, '_blank');
    clearCart();
    setIsDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-600 p-4">{error}</div>;
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-pink-50/90 via-white to-rose-50/50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              Brissbella Wellness Store
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Discover our exclusive range of natural beauty and wellness
              products. Each product is carefully crafted to enhance your
              natural beauty while maintaining the highest standards of quality
              and care.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {/* Category Filter */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-md shadow-sm">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                  selectedCategory === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } border border-gray-200`}
              >
                All Products
              </button>
              {Object.keys(products).map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 text-sm font-medium ${
                    selectedCategory === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } border-t border-b border-r border-gray-200 ${
                    category === Object.keys(products).slice(-1)[0]
                      ? 'rounded-r-lg'
                      : ''
                  }`}
                >
                  {category
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {selectedCategory === 'all'
              ? Object.values(products)
                  .flat()
                  .map((product) => (
                    <div key={product.id} className="h-full">
                      <ProductCard product={product} />
                    </div>
                  ))
              : products[selectedCategory]?.map((product) => (
                  <div key={product.id} className="h-full">
                    <ProductCard product={product} />
                  </div>
                ))}
          </div>

          {/* Cart Button */}
          {Object.keys(cart).length > 0 && (
            <div className="fixed bottom-4 right-4 z-50">
              <button
                onClick={() => setIsDialogOpen(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span className="font-medium">
                  Order Now ({Object.values(cart).reduce((a, b) => a + b, 0)})
                </span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              Have a Beauty Concern?
            </h2>
            <p className="text-xl text-gray-700 mt-4">
              Want to know which product is right for you? Our beauty experts
              are here to help you find the perfect solution for your needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="https://wa.me/94776620717?text=Hi,%20I'd%20like%20to%20know%20more%20about%20which%20Brissbella%20products%20would%20be%20best%20for%20me."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-green-600 text-white py-4 px-6 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Chat with an Expert
            </a>
            <a
              href="tel:+94776620717"
              className="flex items-center justify-center gap-2 bg-primary-600 text-white py-4 px-6 rounded-lg text-lg font-medium hover:bg-primary-700 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Get Expert Advice
            </a>
          </div>
        </div>
      </section>

      {/* Delivery Information Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Delivery Information */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-primary-600">
                Delivery Information
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center">
                  <span className="mr-2">🚚</span>
                  Island-wide delivery available
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✈️</span>
                  International shipping available
                </li>
                <li className="flex items-center">
                  <span className="mr-2">🏪</span>
                  Direct pickup options
                </li>
                <li className="flex items-center">
                  <span className="mr-2">📦</span>
                  Secure packaging
                </li>
              </ul>
            </div>

            {/* Product Quality & Care */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-primary-600">
                Quality & Care
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">✨</span>
                  Premium quality natural ingredients
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🌿</span>
                  Herbal-based formulations
                </li>
                <li className="flex items-start">
                  <span className="mr-2">⭐</span>
                  Dermatologically tested
                </li>
                <li className="flex items-start">
                  <span className="mr-2">💯</span>
                  100% authentic products
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📝</span>
                  Detailed usage instructions included
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <OrderConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        cart={cart}
        products={products}
        onConfirm={handleOrder}
        type="product"
      />
    </div>
  );
};

export default StorePage;
